import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, setToken, getToken, errMsg } from "@/lib/api";

const EMPTY_CART = { items: [], subtotal: 0, delivery_fee: 0, handling_fee: 0, discount: 0, total: 0, amount_to_free_delivery: 0, applied_coupon: null, charges: [] };
const Ctx = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [location, setLocationState] = useState(localStorage.getItem("jb_location") || "Ahmedabad, Gujarat");
  const [locationPin, setLocationPin] = useState(localStorage.getItem("jb_location_pin") || "");
  const [cart, setCart] = useState(EMPTY_CART);
  const [wishlist, setWishlist] = useState([]);
  const [eta, setEta] = useState(45);
  const [etaEnabled, setEtaEnabled] = useState(true);

  const setLocation = (s, pin) => {
    setLocationState(s); localStorage.setItem("jb_location", s);
    setLocationPin(pin || ""); localStorage.setItem("jb_location_pin", pin || "");
  };
  const refreshCart = useCallback(async () => { try { setCart(await api.get("/cart")); } catch {} }, []);
  const refreshWishlist = useCallback(async () => { try { setWishlist((await api.get("/wishlist")).map((p) => p.id)); } catch {} }, []);

  useEffect(() => {
    (async () => {
      api.get("/eta").then((r) => { setEta(r.minutes || 45); setEtaEnabled(r.enabled !== false); }).catch(() => {});
      if (getToken()) {
        try { setUser(await api.get("/auth/me")); refreshCart(); refreshWishlist(); } catch (e) { if (/"status"|Unauthorized|HTTP 40[13]/.test(e.message || "")) setToken(""); }
      }
      setReady(true);
    })();
  }, [refreshCart, refreshWishlist]);

  const login = async (mobile, otp) => {
    const res = await api.post("/auth/mobile/login", { mobile, otp: otp || null });
    if (res.otp_required) return res;
    setToken(res.token); setUser(res.user); refreshCart(); refreshWishlist();
    return res.user;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setToken(""); setUser(null); setCart(EMPTY_CART); setWishlist([]);
  };
  const addToCart = async (productId, qty = 1) => {
    if (!user) { toast("Please login to add items to your cart"); window.location.assign(`/login?next=${encodeURIComponent(window.location.pathname)}`); return; }
    try { setCart(await api.post("/cart/add", { product_id: productId, quantity: qty })); }
    catch (e) { toast.error(errMsg(e, "Could not update cart")); }
  };
  const removeFromCart = async (productId) => {
    const item = cart.items.find((i) => i.id === productId);
    if (item) setCart(await api.post("/cart/add", { product_id: productId, quantity: -item.quantity }));
  };
  const toggleWishlist = async (productId) => {
    if (!user) { toast("Login to save favourites"); return; }
    if (wishlist.includes(productId)) { await api.del(`/wishlist/${productId}`); setWishlist((w) => w.filter((x) => x !== productId)); }
    else { await api.post(`/wishlist/${productId}`); setWishlist((w) => [...w, productId]); }
  };
  const qtyOf = (id) => cart.items.find((i) => i.id === id)?.quantity || 0;
  const cartCount = cart.items.reduce((n, i) => n + i.quantity, 0);

  return (
    <Ctx.Provider value={{ user, ready, location, locationPin, setLocation, login, logout, updateUser: setUser, cart, refreshCart, addToCart, removeFromCart, qtyOf, cartCount, eta, etaEnabled, wishlist, toggleWishlist, isWished: (id) => wishlist.includes(id) }}>
      {children}
    </Ctx.Provider>
  );
}
export const useApp = () => useContext(Ctx);
